import { BooleanRender } from 'app/components/BasicTable/components/BooleanRender';
import * as React from 'react';
import { ExternalLink, Link } from 'app/components/ExternalLink';
import { TFunction } from 'i18next';
import { translations } from 'locales/translations';
import { Fragment } from 'react';
import { Entity } from 'types/common';
import { AllowedSettings, GlobalSettings } from 'utils/globalSettings';
import {
  ArrayRenderer,
  AssetDetailsImageRender,
  DateRenderer,
  HtmlRenderer,
  UsersRenderer,
} from './AttributeRenderers';
import { AssetRowState, IAssetRow } from './IAssetRow';
import { dateUtils } from 'utils/date-utils';
import { getAssetImageSrc } from 'app/components/AssetThumbImage/getAssetImageSrc';
import { AssetLocationLastUpdate } from './AssetLocationLastUpdate';
import { DoubleRender } from 'app/components/BasicTable/components/DoubleRenderer';
import { H4 } from 'app/components/Typography';
import { bookitColors } from 'styles/colors/bookitColors';
import { Tooltip } from '@material-ui/core';
import { IconButton } from 'app/components/BasicButtons/IconButton';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { IAssetAssemblyStructure } from './IAssetAssemblyStructure';
import { AssetComments } from './components/AssetComments';
import { BookitChip } from 'app/components/BasicChip';
import { SidePanelOpenState } from 'app/hooks/useSidePanelOpen';
import { AssetsSelectionProps } from '../MyAssetsPage/AssetsSelection/AssetsSelection';
import { Condition, ODataOperators } from 'api/odata/ODataFilter';
import { IMyAssetsRow } from '../MyAssetsPage/IMyAssetsRow';
import { RenderPageType } from 'app/Layout/FrontendLayout/slice/type';
import { Button } from 'app/components/BasicButtons/Button';
import { getAssetEditLink } from './getAssetEditLink';
import { AssetLink } from './OpenAssetDetails';
import { Skeleton } from '@material-ui/lab';

export const getRows = (
  t: TFunction,
  setting: GlobalSettings,
  openPanel: (state: SidePanelOpenState) => void,
  publicUrl?: string,
  isCover?: boolean,
  useSidePanel?: boolean,
  // setCover?: Dispatch<SetStateAction<ReactNode>>,
  isAdmin?: boolean,
  isMobile?: boolean,
  isAuthenticated?: boolean,
): IAssetRow[] => {
  return [
    {
      Id: 'ImageName',
      PropName: 'ImageName',
      Name: t(translations.Image) as string,
      Cell: ({ value, original }) =>
        !!value &&
        AssetDetailsImageRender({
          value: getAssetImageSrc(
            value,
            undefined,
            (setting.GetNullableByKey(AllowedSettings.AssetImageSizeType) ??
              '2') === '1' && !isMobile
              ? '/images/400x300.png'
              : '/images/200x150.png',
          ),
          original: original,
          shortView: isMobile,
        }),

      // <img
      //   src={getAssetImageSrc(
      //     value,
      //     undefined,
      //     (setting.GetNullableByKey(AllowedSettings.AssetImageSizeType) ??
      //       '2') === '1' && !isMobile
      //       ? '/images/400x300.png'
      //       : '/images/200x150.png',
      //   )}
      //   alt=""
      //   style={
      //     (setting.GetNullableByKey(AllowedSettings.AssetImageSizeType) ??
      //       '2') === '1' && !isMobile
      //       ? { maxWidth: '400px', maxHeight: '300px' }
      //       : { maxWidth: '200px', maxHeight: '150px' }
      //   }
      // />
      NoHeader: true,
      validToRender: ({ value }) => !!value,
    },
    {
      Id: 'ServiceGroup',
      Name: t(translations.ServiceGroup) as string,
      Cell: ({ value, original }) => {
        const editLink = getAssetEditLink(
          'ServiceGroup',
          original.Id,
          original.ServiceId ?? undefined,
          original.ServiceGroup?.Id ?? undefined,
          original.ServiceGroup?.DivisionId ?? undefined,
        );
        return (
          <Button
            style={{ justifyContent: 'left' }}
            size={'small'}
            variant="text"
            href={
              original.AvailabilityType.Id === 'ServiceGroup'
                ? editLink
                : undefined
            }
            target={
              original.AvailabilityType.Id === 'ServiceGroup'
                ? '_blank'
                : undefined
            }
            component={
              original.AvailabilityType.Id === 'ServiceGroup' ? 'a' : undefined
            }
            onClick={
              original.AvailabilityType.Id === 'ServiceGroup'
                ? undefined
                : () =>
                    openPanel({
                      renderPageType: RenderPageType.AssetDetails,
                      renderPageProps: {
                        identifier: {
                          id: original.ServiceGroup.AssetId ?? undefined,
                        },
                        useSidePanel: true,
                      },
                      expanded: false,
                      useSidePanel: useSidePanel,
                      isCover: useSidePanel,
                    })
            }
          >
            {value.Name}
          </Button>
        );
      },
      // Cell: ({ value, original }) => {
      //   const editLink = getAssetEditLink(
      //     'ServiceGroup',
      //     original.Id,
      //     original.ServiceId ?? undefined,
      //     original.ServiceGroup?.Id ?? undefined,
      //     original.ServiceGroup?.DivisionId ?? undefined,
      //   );
      //   if (editLink !== undefined) {
      //     return (
      //       <Button
      //         style={{ justifyContent: 'left' }}
      //         size={'small'}
      //         variant="text"
      //         component={'a'}
      //         href={editLink}
      //         target={'_blank'}
      //       >
      //         {value.Name}
      //       </Button>
      //     );
      //   }
      // },
    },
    {
      Id: 'AvailabilityType',
      PropName: 'AvailabilityType',
      Name: t(translations.Availability),
      Cell: ({ value, original }) => {
        return (
          <Fragment>
            {original.ApprovalRequired ? (
              <Fragment>
                {`${value.Name} - ${t(translations.ApprovalRequired)}`}
              </Fragment>
            ) : (
              <Fragment>{value.Name}</Fragment>
            )}
          </Fragment>
        );
      },
    },
    {
      Id: 'ForceTutoring',
      Name: t(translations.TrainingMandatory) as string,
      validToRender: ({ value, original }) =>
        value === true && original.NotAllowReservations === false,
      Cell: ({ value }) => {
        return (
          <Fragment>
            <strong>{t(translations.ApprovalRequired)}</strong>
          </Fragment>
        );
      },
    },
    {
      Id: 'minorder',
      Name: t(translations.MinimumReservationDurationH) as string,
      validToRender: ({ value, original }) =>
        !!value && value > 0 && original.NotAllowReservations === false,
      Cell: DoubleRender,
    },
    {
      Id: 'maxorder',
      Name: t(translations.MaximumReservationDurationH) as string,
      validToRender: ({ value, original }) =>
        !!value && value > 0 && original.NotAllowReservations === false,
      Cell: DoubleRender,
    },
    {
      Id: 'ReservationMinimumGap',
      Name: t(translations.ReservationMinimumGap) as string,
      validToRender: ({ value, original }) =>
        !!value && value > 0 && original.NotAllowReservations === false,
      Cell: DoubleRender,
    },
    {
      Id: 'CarryOverTimeSlotTypeId',
      Name: t(translations.CarryOverTimeSlotGap) as string,
      validToRender: ({ value, original }) =>
        !!value &&
        value !== null &&
        value > 0 &&
        original.NotAllowReservations === false,
      Cell: ({ value, original }) => {
        return (
          <>{`${original.CarryOverTimeSlotGap} hours over timeslot ${original.CarryOverTimeSlotTypeName}`}</>
        );
      },
      dependencies: ['CarryOverTimeSlotGap', 'CarryOverTimeSlotTypeName'],
    },
    // {
    //   Id: 'MinimumTimeAhead',
    //   Name: t(translations.MinimumTimeAhead) as string,
    //   validToRender: ({ value, original }) => {
    //     return (
    //       original.ReservationEnabled &&
    //       ((original.MinTimeAheadPeriodType !== undefined &&
    //         !!original.MinTimeAheadPeriodValue) ||
    //         !!original.MinTimeAheadMinutesSinceMidnight)
    //     );
    //   },
    //   Cell: ({ value, original }) => {
    //     let results = '';
    //     if (!!original.MinTimeAheadPeriodValue) {
    //       results = `${t(
    //         translations.Before,
    //       )}: ${original.MinTimeAheadPeriodValue.toFixed(2)}
    //          ${(t(translations.AdvancedReservation) as string).replace(
    //            '{0}',
    //            enumToEntityArray(MinTimeAheadPeriodTypes).filter(
    //              f => f.Id === original.MinTimeAheadPeriodType,
    //            )[0].Name,
    //          )}`;
    //     } else if (!!original.MinTimeAheadMinutesSinceMidnight) {
    //       results = `${t(translations.OnTheSameDay)}: ${(t(
    //         translations.ToBeBookedBy,
    //       ) as string).replace(
    //         '{0}',
    //         dateUtils.dateFnsUtils.format(
    //           new Date(0, 0, 0, 0, original.MinTimeAheadMinutesSinceMidnight),
    //           dateUtils.DateIOFormats.fullDateTime24h,
    //         ),
    //       )}`;
    //     }
    //     if (results !== '') {
    //       return (
    //         <Fragment>
    //           {results}
    //           <Tooltip
    //             title={
    //               !!original.MinTimeAheadPeriodValue
    //                 ? t(translations.AdvancedReservation_info)
    //                 : t(translations.ToBeBookedBy_info)
    //             }
    //           >
    //             <span style={{ marginLeft: '1ch' }}>
    //               <Icon icon="info-circle" />
    //             </span>
    //           </Tooltip>
    //         </Fragment>
    //       );
    //     }
    //   },
    // },
    // {
    //   Id: 'RestrictedReservationsOption',
    //   Name: t(translations.InstrumentRestrictions) as string,
    //   validToRender: ({ value, original }) => {
    //     return (
    //       (value !== null &&
    //         value !== (RestrictedReservationsOptions.NoLimitations as number) &&
    //         original.ReservationEnabled &&
    //         original.ServiceType === 'Online' &&
    //         !setting.GetBooleanByKey(
    //           AllowedSettings.HideInstrumentRestrictions,
    //         ) &&
    //         original.AssemblyId === null) ||
    //       original.PrerequisiteEquipments?.length > 0
    //     );
    //   },
    //   //dependencies: ['PrerequisiteEquipments'],
    //   Cell: ({ value, original }) => {
    //     return (
    //       <Tooltip title={t(translations.ShowRestrictionsFull)}>
    //         <MuiLink
    //           style={{ cursor: 'pointer' }}
    //           onClick={() =>
    //             !!setCover &&
    //             setCover(() => {
    //               return (
    //                 <InstrumentRestrictions
    //                   serviceId={original.ServiceId ?? -1}
    //                   serviceGroupId={original.ServiceGroup?.Id}
    //                   useSidePanel
    //                   isCover
    //                   closeCover={() => setCover(undefined)}
    //                   onClose={() => setCover(undefined)}
    //                 />
    //               );
    //             })
    //           }
    //         >
    //           {t(translations.CheckTrainingStatus)}
    //         </MuiLink>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      Id: 'Division',
      Name: t(translations.AssetsInstituteBusiness) as string,
      Cell: ({ original }) => original.ServiceGroup.DivisionName,
    },
    {
      Id: 'AssetAccessories',
      Name: t(translations.Accessories) as string,
      Cell: ArrayRenderer<Entity<number>>(v => <>{v.Name}</>),
    },
    {
      Id: 'AssetCatGroup',
      Name: t(translations.AssetCatGroupName) as string,
      Cell: ({ value, original }) => {
        if (
          !!value &&
          setting.GetBooleanByKey(AllowedSettings.AssetCatEnabled)
        ) {
          return (
            <div>
              {value}
              {isAuthenticated && (
                <Tooltip title={t(translations.SeeAllRelatedAssets) as string}>
                  <span>
                    <IconButton
                      aria-label="link"
                      variant="ghost"
                      shape="square"
                      size="small"
                      title={''}
                      onClick={() => {
                        let setProps = {
                          useSidePanel: true,
                          actionType: 'AssetSearch',
                          assetCatGroupId: original.AssetCat.GroupId,
                          customPredicates: [
                            `${new Condition<IMyAssetsRow>(
                              'AssetCatGroupId',
                              ODataOperators.Equals,
                              original.AssetCat.GroupId,
                            ).toString()}`,
                          ],
                          hideCoresToggle: true,
                        } as AssetsSelectionProps;

                        openPanel({
                          renderPageType: RenderPageType.AssetsSelection,
                          renderPageProps: setProps,
                          expanded: !useSidePanel,
                          isCover: isCover,
                          useSidePanel: !isCover,
                        } as SidePanelOpenState);
                      }}
                      // onClick={() =>
                      //   window.open(
                      //     toRootedURL(
                      //       '/AssetSearch?active=true&AssetCatGroupId=' +
                      //         original.AssetCat.GroupId,
                      //     ),
                      //     '_blank',
                      //   )
                      // }
                    >
                      <Icon icon="arrow-up-right-from-square" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          );
        }
      },
    },
    {
      Id: 'AssetCat',
      Name: t(translations.AssetCategory) as string,
      PropName: 'AssetCat',
      validToRender: ({ value }) => {
        return (
          !!value && setting.GetBooleanByKey(AllowedSettings.AssetCatEnabled)
        );
      },
      Cell: ({ original }) => {
        return (
          <div>
            {original.AssetCat.Name}

            <>
              {!!original.AssetCat.InternalServiceAvaliable && (
                <>{t(translations.InternalServiceAvaliable)}</>
              )}
            </>
            {isAuthenticated && (
              <Tooltip title={t(translations.SeeAllRelatedAssets) as string}>
                <span>
                  <IconButton
                    aria-label="link"
                    variant="ghost"
                    shape="square"
                    size="small"
                    title={''}
                    onClick={() => {
                      let setProps = {
                        useSidePanel: true,
                        actionType: 'AssetSearch',
                        assetCatId: original.AssetCat.Id,
                        customPredicates: [
                          `${new Condition<IMyAssetsRow>(
                            'AssetCatId',
                            ODataOperators.Equals,
                            original.AssetCat.Id,
                          ).toString()}`,
                        ],
                        hideCoresToggle: true,
                      } as AssetsSelectionProps;

                      openPanel({
                        renderPageType: RenderPageType.AssetsSelection,
                        renderPageProps: setProps,
                        expanded: !useSidePanel,
                        isCover: isCover,
                        useSidePanel: !isCover,
                      } as SidePanelOpenState);
                    }}
                    // onClick={() =>
                    //   window.open(
                    //     toRootedURL(
                    //       '/AssetSearch?active=true&AssetCatId=' +
                    //         original.AssetCat.Id,
                    //     ),
                    //     '_blank',
                    //   )
                    // }
                  >
                    <Icon icon="arrow-up-right-from-square" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      Id: 'NickName',
      Name: t(translations.NickName) as string,
    },
    {
      Id: 'OtherNames',
      Name: t(translations.OtherNames) as string,
    },
    {
      Id: 'Brand',
      Name: t(translations.Brand),
    },
    {
      Id: 'Model',
      Name: t(translations.Model) as string,
    },
    {
      Id: 'ModelID',
      Name: t(translations.Model) as string,
      validToRender: ({ value }) => !!value,
      Cell: ({ original, value }) => (
        <Link
          href={'/Assets/AssetModelDetails/Details.aspx?id' + value}
          target="_blank"
        >
          {value}
        </Link>
      ),
    },
    {
      Id: 'ModelNumber',
      Name: t(translations.ModelNumber) as string,
    },
    {
      Id: 'Campus',
      Name: t(translations.Campus) as string,
      validToRender: ({ value, original }) => !!value && value !== null,
      Cell: ({ value }) => <Fragment>{value.Name}</Fragment>,
    },
    {
      Id: 'Building',
      Name: t(translations.Building) as string,
    },
    {
      Id: 'Room',
      Name: t(translations.RoomNumber) as string,
      validToRender: ({ value, original }) => !!value,
      Cell: ({ value, original }) => (
        <>
          {!!value && <>{value}</>}
          {isAdmin === true && (
            <AssetLocationLastUpdate assetId={original.Id} />
          )}
        </>
      ),
    },
    {
      Id: 'LocationsList',
      Name: t(translations.LocationsList) as string,
      PropName: 'LocationsList',
    },
    {
      Id: 'SubLocationName',
      Name: t(translations.LocationsList) as string,
      PropName: 'SubLocationsList',
    },
    {
      Id: 'Experts',
      Name: t(translations.EquipmentExpert) as string,
      validToRender: ({ value }) => !!value && value.length > 0,
      Cell: UsersRenderer(),
    },
    {
      Id: 'Contacts',
      Name: t(translations.ContactsPersonForEquipment) as string,
      validToRender: ({ value }) => !!value && value.length > 0,
      Cell: UsersRenderer(),
    },
    {
      Id: 'Suppliers',
      Name: t(translations.ExternalCompany) as string,
      validToRender: ({ value }) => !!value && value.length > 0,
      Cell: ArrayRenderer<Entity<string>>(v => <>{v.Name}</>),
    },
    {
      Id: 'MainSpecRange',
      Name: t(translations.MainSpecificationRange) as string,
    },
    {
      Id: 'ElectricalSpecs',
      Name: t(translations.ElectricalSpecs) as string,
    },
    {
      Id: 'FunctionalCharacteristics',
      Name: t(translations.FunctionalCharacteristics) as string,
    },
    {
      Id: 'Description',
      Name: t(translations.Description) as string,
      Cell: HtmlRenderer,
    },
    {
      Id: 'WebLink',
      Name: t(translations.WebLink) as string,
      Cell: ({ value }) => value && <Link href={value}>{value}</Link>,
    },
    {
      Id: 'Raac',
      Name: t(translations.RoutineActiveAnalysisCapable) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'Tags',
      Name: t(translations.Tags) as string,
      validToRender: ({ value }) => !!value && value.length > 0,
      Cell: ArrayRenderer<Entity<number>>(v => <>{v.Name}</>),
    },
    {
      Id: 'ExtraDate0',
      Name: t(translations.Asset_ExtraDate0) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate1',
      Name: t(translations.Asset_ExtraDate1) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate2',
      Name: t(translations.Asset_ExtraDate2) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate3',
      Name: t(translations.Asset_ExtraDate3) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate4',
      Name: t(translations.Asset_ExtraDate4) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate5',
      Name: t(translations.Asset_ExtraDate5) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate6',
      Name: t(translations.Asset_ExtraDate6) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate7',
      Name: t(translations.Asset_ExtraDate7) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate8',
      Name: t(translations.Asset_ExtraDate8) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraDate9',
      Name: t(translations.Asset_ExtraDate9) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'ExtraText0',
      Name: t(translations.Asset_ExtraText0) as string,
    },
    {
      Id: 'ExtraText1',
      Name: t(translations.Asset_ExtraText1) as string,
    },
    {
      Id: 'ExtraText2',
      Name: t(translations.Asset_ExtraText2) as string,
    },
    {
      Id: 'ExtraText3',
      Name: t(translations.Asset_ExtraText3) as string,
    },
    {
      Id: 'ExtraText4',
      Name: t(translations.Asset_ExtraText4) as string,
    },
    {
      Id: 'ExtraText5',
      Name: t(translations.Asset_ExtraText5) as string,
    },
    {
      Id: 'ExtraText6',
      Name: t(translations.Asset_ExtraText6) as string,
    },
    {
      Id: 'ExtraText7',
      Name: t(translations.Asset_ExtraText7) as string,
    },
    {
      Id: 'ExtraText8',
      Name: t(translations.Asset_ExtraText8) as string,
    },
    {
      Id: 'ExtraText9',
      Name: t(translations.Asset_ExtraText9) as string,
    },
    {
      Id: 'ExtraText10',
      Name: t(translations.Asset_ExtraText10) as string,
    },
    {
      Id: 'ExtraText11',
      Name: t(translations.Asset_ExtraText11) as string,
    },
    {
      Id: 'ExtraText12',
      Name: t(translations.Asset_ExtraText12) as string,
    },
    {
      Id: 'ExtraText13',
      Name: t(translations.Asset_ExtraText13) as string,
    },
    {
      Id: 'ExtraText14',
      Name: t(translations.Asset_ExtraText14) as string,
    },
    {
      Id: 'ExtraText15',
      Name: t(translations.Asset_ExtraText15) as string,
    },
    {
      Id: 'ExtraInt0',
      Name: t(translations.Asset_ExtraInt0) as string,
    },
    {
      Id: 'ExtraInt1',
      Name: t(translations.Asset_ExtraInt1) as string,
    },
    {
      Id: 'ExtraInt2',
      Name: t(translations.Asset_ExtraInt2) as string,
    },
    {
      Id: 'ExtraInt3',
      Name: t(translations.Asset_ExtraInt3) as string,
    },
    {
      Id: 'ExtraInt4',
      Name: t(translations.Asset_ExtraInt4) as string,
    },
    {
      Id: 'ExtraDec0',
      Name: t(translations.Asset_ExtraDec0) as string,
    },
    {
      Id: 'ExtraDec1',
      Name: t(translations.Asset_ExtraDec1) as string,
    },
    {
      Id: 'ExtraDec2',
      Name: t(translations.Asset_ExtraDec2) as string,
    },
    {
      Id: 'ExtraDec3',
      Name: t(translations.Asset_ExtraDec3) as string,
    },
    {
      Id: 'ExtraDec4',
      Name: t(translations.Asset_ExtraDec4) as string,
    },
    {
      Id: 'ExtraBool0',
      Name: t(translations.Asset_ExtraBool0) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'ExtraBool1',
      Name: t(translations.Asset_ExtraBool1) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'ExtraBool2',
      Name: t(translations.Asset_ExtraBool2) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'ExtraBool3',
      Name: t(translations.Asset_ExtraBool3) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'ExtraBool4',
      Name: t(translations.Asset_ExtraBool4) as string,
      Cell: BooleanRender,
    },
    {
      Id: 'ExtraUrl0',
      Name: t(translations.Asset_ExtraUrl0) as string,
    },
    {
      Id: 'ExtraUrl1',
      Name: t(translations.Asset_ExtraUrl1) as string,
    },
    {
      Id: 'ExtraUrl2',
      Name: t(translations.Asset_ExtraUrl2) as string,
    },
    {
      Id: 'ExtraUrl3',
      Name: t(translations.Asset_ExtraUrl3) as string,
    },
    {
      Id: 'ExtraUrl4',
      Name: t(translations.Asset_ExtraUrl4) as string,
    },
    {
      Id: 'ExtraRichText0',
      Name: t(translations.Asset_ExtraRichText0) as string,
    },
    {
      Id: 'ExtraRichText1',
      Name: t(translations.Asset_ExtraRichText1) as string,
    },
    {
      Id: 'ExtraRichText2',
      Name: t(translations.Asset_ExtraRichText2) as string,
    },
    {
      Id: 'ExtraRichText3',
      Name: t(translations.Asset_ExtraRichText3) as string,
    },
    {
      Id: 'ExtraRichText4',
      Name: t(translations.Asset_ExtraRichText4) as string,
    },
    {
      Id: 'ExtraDDL0',
      Name: t(translations.Asset_ExtraDDL0) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL0?.Name}</>,
    },
    {
      Id: 'ExtraDDL1',
      Name: t(translations.Asset_ExtraDDL1) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL1?.Name}</>,
    },
    {
      Id: 'ExtraDDL2',
      Name: t(translations.Asset_ExtraDDL2) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL2?.Name}</>,
    },
    {
      Id: 'ExtraDDL3',
      Name: t(translations.Asset_ExtraDDL3) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL3?.Name}</>,
    },
    {
      Id: 'ExtraDDL4',
      Name: t(translations.Asset_ExtraDDL4) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL4?.Name}</>,
    },
    {
      Id: 'ExtraDDL5',
      Name: t(translations.Asset_ExtraDDL5) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL5?.Name}</>,
    },
    {
      Id: 'ExtraDDL6',
      Name: t(translations.Asset_ExtraDDL6) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL6?.Name}</>,
    },
    {
      Id: 'ExtraDDL7',
      Name: t(translations.Asset_ExtraDDL7) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL7?.Name}</>,
    },
    {
      Id: 'ExtraDDL8',
      Name: t(translations.Asset_ExtraDDL8) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL8?.Name}</>,
    },
    {
      Id: 'ExtraDDL9',
      Name: t(translations.Asset_ExtraDDL9) as string,
      Cell: ({ value, original }) => !!value && <>{original.ExtraDDL9?.Name}</>,
    },
    {
      Id: 'ExtraDDL10',
      Name: t(translations.Asset_ExtraDDL10) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL10?.Name}</>,
    },
    {
      Id: 'ExtraDDL11',
      Name: t(translations.Asset_ExtraDDL11) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL11?.Name}</>,
    },
    {
      Id: 'ExtraDDL12',
      Name: t(translations.Asset_ExtraDDL12) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL12?.Name}</>,
    },
    {
      Id: 'ExtraDDL13',
      Name: t(translations.Asset_ExtraDDL13) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL13?.Name}</>,
    },
    {
      Id: 'ExtraDDL14',
      Name: t(translations.Asset_ExtraDDL14) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL14?.Name}</>,
    },
    {
      Id: 'ExtraDDL15',
      Name: t(translations.Asset_ExtraDDL15) as string,
      Cell: ({ value, original }) =>
        !!value && <>{original.ExtraDDL15?.Name}</>,
    },
    {
      Id: 'BrandCode',
      Name: t(translations.BrandBarcodeNumber) as string,
    },
    {
      Id: 'ServiceContract',
      Name: t(translations.ServiceContract) as string,
    },
    {
      Id: 'POnumber',
      Name: t(translations.PurchaseOrder) as string,
    },
    {
      Id: 'WarrantyEndDate',
      Name: t(translations.WarrantyEndDate) as string,
      Cell: DateRenderer,
    },
    {
      Id: 'LifeDate',
      Name: t(translations.LifeDateView) as string,
      validToRender: ({ value }) => !!value,
      Cell: ({ value, original }) =>
        value !== null && <>{dateUtils.formatDistanceToNow(value)}</>,
    },
    {
      Id: 'ExportLicense',
      Name: t(translations.ExportLicense) as string,
    },
    {
      Id: 'InternalCode',
      Name: t(translations.InternalEquipmentBarcodeNumber) as string,
    },
    {
      Id: 'AccountingId',
      Name: t(translations.AccountingId) as string,
    },
    {
      Id: 'Active',
      Name: t(translations.Active) as string,
      Cell: ({ value }) => <BooleanRender value={value === true} />,
    },
    {
      Id: 'Details',
      Name: t(translations.AssetDetails) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetDetails')}
        </H4>
      ),
    },
    {
      Id: 'Classification',
      Name: t(translations.AssetClassification) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetClassification')}
        </H4>
      ),
    },
    {
      Id: 'Location',
      Name: t(translations.AssetLocation) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetLocation')}
        </H4>
      ),
    },
    {
      Id: 'SectionContacts',
      Name: t(translations.AssetContacts) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetContacts')}
        </H4>
      ),
    },
    {
      Id: 'Identifiers',
      Name: t(translations.AssetIdentifiers) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetIdentifiers')}
        </H4>
      ),
    },
    {
      Id: 'AdditionalInfo',
      Name: t(translations.AssetAdditionalInfo) as string,
      State: AssetRowState.SectionName,
      Cell: ({ value, original }) => (
        <H4 style={{ fontWeight: 'bolder', lineHeight: '164%' }}>
          {t('AssetAdditionalInfo')}
        </H4>
      ),
    },
    {
      Id: 'SectionLabel7',
      Name: t(translations.AssetSectionLabel7) as string,
      State: AssetRowState.SectionName,
    },
    {
      Id: 'SectionLabel8',
      Name: t(translations.AssetSectionLabel8) as string,
      State: AssetRowState.SectionName,
    },
    {
      Id: 'SectionLabel9',
      Name: t(translations.AssetSectionLabel9) as string,
      State: AssetRowState.SectionName,
    },
    {
      Id: 'SectionLabel10',
      Name: t(translations.AssetSectionLabel10) as string,
      State: AssetRowState.SectionName,
    },
    {
      Id: 'DOI',
      Name: t(translations.Doi) as string,
      Cell: ({ value, original }) => {
        const doiURL = `https://doi.org/${value}`;
        return (
          <Tooltip title={t(translations.AssetDoiTooltip) as string}>
            <span>
              <ExternalLink showIcon href={doiURL}>
                {value}
              </ExternalLink>
            </span>
          </Tooltip>
        );
      },
    },
    {
      Id: 'Status',
      Name: t(translations.Status) as string,
      Cell: ({ value, original }) => {
        let color = bookitColors.base.black;
        let backgroundColor = bookitColors.base.white;
        switch (original.StatusId) {
          case 1:
            color = bookitColors.success.dark;
            backgroundColor = bookitColors.success.background;
            break;
          case 2:
            color = bookitColors.warning.dark;
            backgroundColor = bookitColors.warning.background;
            break;
          case 3:
            color = bookitColors.danger.dark;
            backgroundColor = bookitColors.danger.background;
            break;
          default:
            color = bookitColors.base.black;
            backgroundColor = bookitColors.base.white;
        }
        const RenderStatus = (props: {}) => {
          return (
            <>
              <BookitChip
                variant="default"
                customsize="small"
                label={
                  <>
                    {value !== ' ' ? (
                      <>{value}</>
                    ) : (
                      <Skeleton
                        width={'40px'}
                        height={'32px'}
                        style={{
                          backgroundColor: bookitColors.grayscale.graySelection,
                        }}
                      />
                    )}
                  </>
                }
                custombgcolor={backgroundColor}
                customcolor={color}
                shape="squar"
                key={'status'}
                notremoved={true}
              />
            </>
          );
        };
        return <RenderStatus />;
      },
    },
    {
      Id: 'AssetAssemblyStructure',
      Name: t(translations.AssemblyStructure) as string,
      Cell: ({ value, original }) => {
        const AssetDetailsLink = (props: { name: string; id: number }) => {
          // const history = useHistory();
          // const dispatch = useDispatch();
          // const { actions: layoutActions } = useLayoutSlice();
          return (
            <AssetLink
              id={props.id}
              key={`${props.name}-${props.id}`}
              title={t(translations.Details)}
              useSidePanel={useSidePanel}
              openPanelWithCover={openPanel}
            >
              {props.name}
            </AssetLink>
            // <MuiLink
            //   style={{ cursor: 'pointer' }}
            //   onClick={() => {
            //     history.push(`/Assets/Details?id=${props.id}`);
            //     dispatch(layoutActions.resetSidePanel());
            //   }}
            // >
            //   {props.name}
            // </MuiLink>
          );
        };
        const renderItem = (item: IAssetAssemblyStructure) => {
          if (item.Children.length > 0) {
            let res: React.ReactNode[] = [];
            for (const v of item.Children) {
              res.push(
                <li>
                  <AssetLink
                    id={v.AssetId}
                    key={`${v.Name}-${v.AssetId}`}
                    title={t(translations.Details)}
                    useSidePanel={useSidePanel}
                    openPanelWithCover={openPanel}
                  >
                    {v.Name}
                  </AssetLink>
                </li>,
              );
              let childRes = renderItem(v);
              if (childRes !== undefined && childRes.length > 0) {
                res.push(
                  <ul style={{ margin: 0 }}>{childRes.map((v, i) => v)}</ul>,
                );
              }
            }
            return res;
          } else return undefined;
        };
        return (
          <>
            <li>
              <AssetDetailsLink name={value.Name} id={value.AssetId} />
            </li>
            <ul style={{ margin: 0 }}>{renderItem(value)}</ul>
          </>
        );
      },
    },
    {
      Id: 'Range',
      Name: t(translations.Range) as string,
      Cell: ({ value, original }) => {
        return (
          <>
            {value.map(v => {
              if (v.MinValue !== undefined) {
                if (v.MaxValue !== undefined) {
                  return (
                    <div>
                      {v.MinValue} {' - '}
                      {v.MaxValue} {'   '}
                      {v.UnitTypeName}
                    </div>
                  );
                } else {
                  return (
                    <div>
                      {v.MinValue} {'   '}
                      {v.UnitTypeName} {'(Min)'}
                    </div>
                  );
                }
              } else {
                if (v.MaxValue !== undefined) {
                  return (
                    <div>
                      {v.MaxValue} {'   '}
                      {v.UnitTypeName} {'(Max)'}
                    </div>
                  );
                } else {
                  return <></>;
                }
              }
            })}
          </>
        );
      },
    },
    {
      Id: 'Comments',
      Name: t(translations.CommentsAndAttachments) as string,
      validToRender: val => {
        return (
          isAuthenticated === true &&
          (!val.original.CommentsAreStaffonly || !!isAdmin)
        );
      },
      State: AssetRowState.Mandatory,
      Cell(value) {
        return (
          <AssetComments
            isAdmin={!!isAdmin}
            assetId={value.original.Id}
            staffonly={!!value.original.CommentsAreStaffonly}
            serviceGroups={
              !!value.original.ServiceGroup
                ? [value.original.ServiceGroup]
                : undefined
            }
          />
        );
      },
    },
    // stub, could be used when full screen asset details will be migrated to react https://prog4biz.atlassian.net/browse/BKT-6902
    // {
    //   Id: 'Articles',
    //   PropName: 'Articles',
    //   Name: t(translations.RelatedPublications),
    //   Cell: ({ value }: { value?: IArticleDto[] }) => {
    //     return <AssetArticles value={value} />;
    //   },
    // },
  ];
};
